import type { IAuthAccountMenuItem } from '@/entities/auth-account.d';
import {
  deniedInteractionAccess,
  findInteractionGroupByRouteKey,
  findInteractionRoute,
  INTERACTION_ROUTE_MANIFEST,
  type InteractionAccess,
  type InteractionGroupAccess,
} from '@/modules/workspace/interaction-access';

export interface WorkspaceMenuItem {
  children?: WorkspaceMenuItem[];
  hasDeclaredChildren?: boolean;
  icon?: string;
  interactionAccessMode?: 'bundle';
  key: string;
  label: string;
  path?: string;
  sourcePath?: string;
}

/** normalizePath 清理菜单路径中的查询、哈希和尾随斜杠，供精确兼容规则复用。 */
function normalizePath(path: string) {
  const pathname = path.trim().split(/[?#]/, 1)[0]?.replace(/\/+$/, '') ?? '';
  if (!pathname) return '';
  return pathname.startsWith('/') ? pathname : `/${pathname}`;
}

/** toTeacherWorkshopCanonicalPath 将已发布的 teacher-studio 深链收敛到 IAM 使用的 teacher-workshop 路由族。 */
function toTeacherWorkshopCanonicalPath(pathname: string) {
  if (pathname === '/teacher-studio') return '/teacher-workshop';
  if (pathname.startsWith('/teacher-studio/')) {
    return `/teacher-workshop${pathname.slice('/teacher-studio'.length)}`;
  }
  if (pathname === '/workspace/teacher-studio') return '/workspace/teacher-workshop';
  if (pathname.startsWith('/workspace/teacher-studio/')) {
    return `/workspace/teacher-workshop${pathname.slice('/workspace/teacher-studio'.length)}`;
  }
  return pathname;
}

/** toAuditRoutePath 将历史审核入口收敛为租户工作台唯一规范地址。 */
export function toAuditRoutePath(path = ''): string {
  const pathname = normalizePath(path);
  return ['/audit', '/workspace/audit'].includes(pathname)
    ? '/workspace/audit'
    : '';
}

/**
 * 工作台旧路由相对于 /workspace 声明，新基座统一补齐租户内路由前缀。
 */
export function toWorkspaceRoutePath(path = ''): string {
  const pathname = toTeacherWorkshopCanonicalPath(normalizePath(path));
  if (!pathname) return '';
  const auditPath = toAuditRoutePath(pathname);
  if (auditPath) return auditPath;
  if (pathname === '/admin' || pathname.startsWith('/admin/')) return '';
  if (pathname === '/system' || pathname.startsWith('/system/')) return '';
  if (pathname === '/resource') return '/workspace/interaction';
  if (pathname.startsWith('/resource/')) {
    return `/workspace/interaction${pathname.slice('/resource'.length)}`;
  }
  if (pathname === '/workspace/resource') return '/workspace/interaction';
  if (pathname.startsWith('/workspace/resource/')) {
    return `/workspace/interaction${pathname.slice('/workspace/resource'.length)}`;
  }
  if (pathname === '/workspace' || pathname.startsWith('/workspace/')) return pathname;
  return `/workspace${pathname}`;
}

/** getMenuLabel 按本地化标题、默认标题、路径的顺序生成稳定菜单文案。 */
function getMenuLabel(item: IAuthAccountMenuItem) {
  return item.titleLocal || item.title || item.path;
}

/** 只保留 workspace 应用可见的一级菜单树，并将子项按权限顺序稳定排列。 */
export function normalizeWorkspaceMenus(
  items: IAuthAccountMenuItem[] = [],
  parentKey = 'workspace',
): WorkspaceMenuItem[] {
  return [...items]
    .filter(item => !item.disabled)
    .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
    .flatMap((item, index) => {
      // 必须在过滤禁用或未知子项前保存声明事实；否则空交集会被误判成根 bundle 并造成权限提升。
      const hasDeclaredChildren = Object.prototype.hasOwnProperty.call(item, 'children');
      const children = normalizeWorkspaceMenus(item.children ?? [], `${parentKey}-${index}`);
      const path = toWorkspaceRoutePath(item.path);
      if (!path && !children.length) return [];
      // repeated children 为空时会被传输层省略，只有 extra 中的显式标记能安全区分旧 bundle 与权限过滤为空。
      const interactionAccessMode = item.extra?.interactionAccessMode === 'bundle'
        ? 'bundle'
        : undefined;

      const label = getMenuLabel(item) || path;
      return [{
        children: children.length ? children : undefined,
        hasDeclaredChildren,
        icon: item.icon,
        interactionAccessMode,
        key: `${parentKey}-${index}-${path || label}`,
        label,
        path: path || undefined,
        sourcePath: item.path || undefined,
      }];
    });
}

/** findFirstWorkspaceMenuPath 返回权限排序后的首个可导航叶子路径。 */
export function findFirstWorkspaceMenuPath(items: WorkspaceMenuItem[]): string {
  for (const item of items) {
    const childPath = item.children?.length ? findFirstWorkspaceMenuPath(item.children) : '';
    if (childPath) return childPath;
    if (item.path && item.path !== '/workspace') return item.path;
  }
  return '';
}

/** isWorkspaceRemotePage 判断权限菜单链是否命中 workspace remote 承载页面及其任意子路由。 */
export function isWorkspaceRemotePage(items: WorkspaceMenuItem[]): boolean {
  const roots = [
    '/workspace/ai',
    '/workspace/audit',
    '/workspace/creation',
    '/workspace/interaction',
    '/workspace/e-learning',
    '/workspace/schedule',
    '/workspace/inspection',
    '/workspace/evaluation',
    '/workspace/preparation',
    '/workspace/class',
    '/workspace/teacher-workshop',
  ];
  return items.some((item) => {
    const path = item.path ?? '';
    const matched = roots.some(root => path === root || path.startsWith(`${root}/`));
    return matched || Boolean(item.children?.length && isWorkspaceRemotePage(item.children));
  });
}

/** findInteractionRoot 在规范菜单树中查找资源空间权限根节点。 */
function findInteractionRoot(items: WorkspaceMenuItem[]): WorkspaceMenuItem | undefined {
  for (const item of items) {
    if (item.path === '/workspace/interaction') return item;
    const matched = item.children ? findInteractionRoot(item.children) : undefined;
    if (matched) return matched;
  }
  return undefined;
}

/** collectInteractionRoutes 按后端菜单顺序递归收集根权限内的正式叶子，并忽略分组节点与重复声明。 */
function collectInteractionRoutes(items: WorkspaceMenuItem[]): InteractionAccess['routes'] {
  const routes: InteractionAccess['routes'][number][] = [];
  const visited = new Set<string>();

  for (const item of items) {
    const route = findInteractionRoute(item.path ?? '');
    if (route && !visited.has(route.key)) {
      visited.add(route.key);
      routes.push({ ...route, label: item.label || route.label });
    }
    if (item.children?.length) {
      for (const childRoute of collectInteractionRoutes(item.children)) {
        if (visited.has(childRoute.key)) continue;
        visited.add(childRoute.key);
        routes.push(childRoute);
      }
    }
  }

  return routes;
}

/** collectInteractionGroups 保留根节点下的后台分组名称，并按分组范围去重正式路由。 */
function collectInteractionGroups(items: WorkspaceMenuItem[]): InteractionGroupAccess[] {
  const groups: InteractionGroupAccess[] = [];
  const groupIndexes = new Map<InteractionGroupAccess['key'], number>();
  const visitedRoutes = new Set<string>();

  for (const item of items) {
    const itemRoutes = collectInteractionRoutes([item]).filter(route => !visitedRoutes.has(route.key));
    const itemGroupKeys = [...new Set(itemRoutes.flatMap((route) => {
      const group = findInteractionGroupByRouteKey(route.key);
      return group ? [group.key] : [];
    }))];

    for (const groupKey of itemGroupKeys) {
      const groupedRoutes = itemRoutes.filter(
        route => findInteractionGroupByRouteKey(route.key)?.key === groupKey,
      );
      if (!groupedRoutes.length) continue;
      groupedRoutes.forEach(route => visitedRoutes.add(route.key));

      const registeredGroup = findInteractionGroupByRouteKey(groupedRoutes[0]!.key);
      if (!registeredGroup) continue;
      // 只有整个后台节点明确承载单一产品分组时才采用它的标题，避免普通目录误改分组名称。
      const label = item.children?.length && itemGroupKeys.length === 1
        ? item.label
        : registeredGroup.label;
      const existingIndex = groupIndexes.get(groupKey);
      if (existingIndex === undefined) {
        groupIndexes.set(groupKey, groups.length);
        groups.push({ key: groupKey, label, routes: groupedRoutes });
        continue;
      }
      const existing = groups[existingIndex]!;
      groups[existingIndex] = {
        ...existing,
        routes: [...existing.routes, ...groupedRoutes],
      };
    }
  }

  return groups;
}

/** resolveInteractionAccess 将原始菜单声明解析成 Remote 唯一可消费的资源空间授权快照。 */
export function resolveInteractionAccess(items: WorkspaceMenuItem[]): InteractionAccess {
  const root = findInteractionRoot(items);
  if (!root) return deniedInteractionAccess;

  const sourcePath = normalizePath(root.sourcePath ?? '');
  if (
    !root.hasDeclaredChildren
    && root.interactionAccessMode === 'bundle'
    && (sourcePath === '/resource' || sourcePath === '/workspace/resource')
  ) {
    return { mode: 'bundle', routes: INTERACTION_ROUTE_MANIFEST };
  }
  if (!root.hasDeclaredChildren) return deniedInteractionAccess;

  const children = root.children ?? [];
  const hasExplicitGroups = children.some(item => (
    Boolean(item.children?.length) && !findInteractionRoute(item.path ?? '')
  ));
  // 只有后台确实声明了纯分组节点才切换新布局；旧租户的扁平 children 保持原展示和排序。
  if (!hasExplicitGroups) {
    const routes = collectInteractionRoutes(children);
    return routes.length ? { mode: 'children', routes } : deniedInteractionAccess;
  }

  // 当前菜单允许在根与叶子之间增加“个人空间/校本空间”等纯分组；递归范围始终限制在已授权根内。
  const groups = collectInteractionGroups(children);
  const routes = groups.flatMap(group => group.routes);
  return routes.length ? { groups, mode: 'children', routes } : deniedInteractionAccess;
}

/** isWorkspaceELearningPath 识别云研社规范深链，供未开放 IAM 菜单时挂载只读安全边界。 */
export function isWorkspaceELearningPath(pathname: string): boolean {
  return pathname === '/workspace/e-learning' || pathname.startsWith('/workspace/e-learning/');
}

/** isWorkspaceNoticePath 让通知中心在 IAM 菜单未声明时仍由固定 Host 入口安全挂载。 */
export function isWorkspaceNoticePath(pathname: string): boolean {
  const path = normalizePath(pathname);
  return path === '/workspace/notice' || path.startsWith('/workspace/notice/');
}

/** isWorkspaceAccountPath 让账号安全深链不依赖工作台菜单即可挂载个人中心 Remote 页面。 */
export function isWorkspaceAccountPath(pathname: string): boolean {
  return normalizePath(pathname) === '/workspace/account';
}

/** isWorkspaceArtifactPath 只识别带稳定 ID 的通用产物详情深链，实际访问权限继续由服务端 owner 校验。 */
export function isWorkspaceArtifactPath(pathname: string): boolean {
  const path = normalizePath(pathname);
  return /^\/workspace\/artifacts\/[A-Za-z0-9_-]{1,15}$/.test(path);
}

/** isInteractionWorkspacePath 让资源空间深链始终挂载 Remote，并由 denied access 呈现 403。 */
export function isInteractionWorkspacePath(pathname: string): boolean {
  const path = normalizePath(pathname);
  return path === '/workspace/interaction' || path.startsWith('/workspace/interaction/');
}

/** toTenantRouterPath 把浏览器完整租户地址还原为 PatternBrowserRouter 内部路径。 */
export function toTenantRouterPath(pathname: string, tenantID: string): string {
  const tenantPrefix = `/t/${tenantID}`;
  if (pathname === tenantPrefix) return '/';
  return pathname.startsWith(`${tenantPrefix}/`)
    ? pathname.slice(tenantPrefix.length)
    : pathname;
}

/** isCreatorCenterRemotePath 让创作者中心旧深链在菜单暂未下发时仍进入 Remote，由服务端接口决定权限。 */
export function isCreatorCenterRemotePath(pathname: string): boolean {
  const path = normalizePath(pathname);
  return path === '/workspace/creation' || path.startsWith('/workspace/creation/');
}

/** isClassSpaceWorkspacePath 让 AI 工作台的班级入口不依赖 IAM 菜单，并由班级接口收口数据范围。 */
export function isClassSpaceWorkspacePath(pathname: string): boolean {
  const path = normalizePath(pathname);
  return path === '/workspace/class' || path.startsWith('/workspace/class/');
}

/** isTeacherStudioWorkspacePath 识别不依赖菜单展示的名师工作室消费与对象管理深链。 */
export function isTeacherStudioWorkspacePath(pathname: string): boolean {
  const path = toTeacherWorkshopCanonicalPath(normalizePath(pathname));
  return path === '/workspace/teacher-workshop' || path.startsWith('/workspace/teacher-workshop/');
}

/** isHrWorkspacePath 识别由独立 HR Workspace Remote 承载的规范子路由。 */
export function isHrWorkspacePath(pathname: string): boolean {
  const path = normalizePath(pathname);
  return path === '/workspace/hr' || path.startsWith('/workspace/hr/');
}

/** findWorkspaceMenuByKey 在权限菜单树中定位交互选中的节点。 */
export function findWorkspaceMenuByKey(items: WorkspaceMenuItem[], key: string): WorkspaceMenuItem | undefined {
  for (const item of items) {
    if (item.key === key) return item;
    const matched = item.children ? findWorkspaceMenuByKey(item.children, key) : undefined;
    if (matched) return matched;
  }
  return undefined;
}

/** pathnameMatchesMenu 允许菜单叶子命中自身及其详情子路径。 */
function pathnameMatchesMenu(pathname: string, menuPath: string) {
  // 地址栏可能仍是已发布的 teacher-studio 深链；匹配前规范化才能保持菜单选中并挂载同一个 Remote。
  const canonicalPathname = toWorkspaceRoutePath(pathname);
  return canonicalPathname === menuPath || canonicalPathname.startsWith(`${menuPath}/`);
}

/** findWorkspaceMenuTrail 返回当前地址命中的最长权限菜单链，避免父级抢占子路由。 */
export function findWorkspaceMenuTrail(items: WorkspaceMenuItem[], pathname: string): WorkspaceMenuItem[] {
  let best: WorkspaceMenuItem[] = [];

  for (const item of items) {
    const childTrail = item.children ? findWorkspaceMenuTrail(item.children, pathname) : [];
    if (childTrail.length) {
      const trail = [item, ...childTrail];
      const trailPath = trail[trail.length - 1]?.path ?? '';
      const bestPath = best[best.length - 1]?.path ?? '';
      if (trailPath.length > bestPath.length) best = trail;
    }

    if (item.path && pathnameMatchesMenu(pathname, item.path)) {
      const bestPath = best[best.length - 1]?.path ?? '';
      if (item.path.length > bestPath.length) best = [item];
    }
  }

  return best;
}
